"use client";

import { c as _c } from "react/compiler-runtime";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { createContext, useContext, useState } from "react";
import type { ComponentProps, ReactNode } from "react";
import { cn } from "../lib/cn";
export function TooltipProvider(t0) {
  const $ = _c(6);
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      delayDuration: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
    $[2] = t1;
  } else {
    props = $[1];
    t1 = $[2];
  }
  const delayDuration = t1 === undefined ? 0 : t1;
  let t2;
  if ($[3] !== delayDuration || $[4] !== props) {
    t2 = <TooltipPrimitive.Provider data-slot="tooltip-provider" delayDuration={delayDuration} {...props} />;
    $[3] = delayDuration;
    $[4] = props;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}
const TooltipContext = createContext<{
  open: boolean;
  setOpen: (open: boolean) => void;
} | null>(null);
export function Tooltip(t0) {
  const $ = _c(12);
  let children;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = props;
  } else {
    children = $[1];
    props = $[2];
  }
  const [open, setOpen] = useState(false);
  let t1;
  if ($[3] !== open) {
    t1 = {
      open,
      setOpen
    };
    $[3] = open;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== children || $[6] !== open || $[7] !== props) {
    t2 = <TooltipPrimitive.Root data-slot="tooltip" open={open} onOpenChange={setOpen} {...props}>{children}</TooltipPrimitive.Root>;
    $[5] = children;
    $[6] = open;
    $[7] = props;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== t1 || $[10] !== t2) {
    t3 = <TooltipProvider><TooltipContext.Provider value={t1}>{t2}</TooltipContext.Provider></TooltipProvider>;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
}
export function TooltipTrigger(t0) {
  const $ = _c(10);
  let children;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = props;
  } else {
    children = $[1];
    props = $[2];
  }
  const context = useContext(TooltipContext);
  if (context === null) {
    throw new Error("TooltipTrigger must be used within a Tooltip component");
  }
  const {
    open,
    setOpen
  } = context;
  let t1;
  if ($[3] !== open || $[4] !== setOpen) {
    t1 = () => {
      setOpen(!open);
    };
    $[3] = open;
    $[4] = setOpen;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== children || $[7] !== props || $[8] !== t1) {
    t2 = <TooltipPrimitive.Trigger data-slot="tooltip-trigger" onClick={t1} {...props}>{children}</TooltipPrimitive.Trigger>;
    $[6] = children;
    $[7] = props;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
}
export function TooltipContent(t0) {
  const $ = _c(16);
  let children;
  let className;
  let props;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      className,
      sideOffset: t1,
      showArrow: t2,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = t1;
    $[5] = t2;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    t1 = $[4];
    t2 = $[5];
  }
  const sideOffset = t1 === undefined ? 0 : t1;
  const showArrow = t2 === undefined ? true : t2;
  let t3;
  if ($[6] !== className) {
    t3 = cn("fade-in-0 zoom-in-95 data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 animate-in bg-primary text-primary-foreground data-[state=closed]:animate-out z-50 w-fit origin-(--radix-tooltip-content-transform-origin) rounded-md px-3 py-1.5 text-xs text-balance", className);
    $[6] = className;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== showArrow) {
    t4 = showArrow && <TooltipPrimitive.Arrow className="bg-primary fill-primary z-50 size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px]" />;
    $[8] = showArrow;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== children || $[11] !== props || $[12] !== sideOffset || $[13] !== t3 || $[14] !== t4) {
    t5 = <TooltipPrimitive.Portal><TooltipPrimitive.Content data-slot="tooltip-content" sideOffset={sideOffset} className={t3} {...props}>{children}{t4}</TooltipPrimitive.Content></TooltipPrimitive.Portal>;
    $[10] = children;
    $[11] = props;
    $[12] = sideOffset;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  return t5;
}